import React from "react";
import { DefaultButton } from "@/components/DefaultButton";
import { toast } from "sonner";

const RECOVERY_NOTICE_STORAGE_KEY = "cfl:moonpay-recovery-notice";

const RECOVERABLE_MESSAGE_FRAGMENT = "externalTransactionId";
const RECOVERABLE_STACK_FRAGMENT = "MoonpayStatusScreen";

function isKnownMoonPayCrash(error: unknown) {
  if (!(error instanceof Error)) {
    return false;
  }

  const matchesMessage = error.message.includes(RECOVERABLE_MESSAGE_FRAGMENT);
  const matchesStack =
    typeof error.stack === "string" &&
    error.stack.includes(RECOVERABLE_STACK_FRAGMENT);

  return matchesMessage && matchesStack;
}

type MoonPayCrashBoundaryProps = {
  children: React.ReactNode;
};

type MoonPayCrashBoundaryState = {
  error: Error | null;
  retryCount: number;
  subtreeKey: number;
};

export class MoonPayCrashBoundary extends React.Component<
  MoonPayCrashBoundaryProps,
  MoonPayCrashBoundaryState
> {
  state: MoonPayCrashBoundaryState = {
    error: null,
    retryCount: 0,
    subtreeKey: 0,
  };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (!isKnownMoonPayCrash(error)) {
      return;
    }

    if (this.state.retryCount > 0) {
      return;
    }

    window.sessionStorage.setItem(
      RECOVERY_NOTICE_STORAGE_KEY,
      String(Date.now()),
    );

    this.setState((state) => ({
      error: null,
      retryCount: state.retryCount + 1,
      subtreeKey: state.subtreeKey + 1,
    }));
  }

  render() {
    const { children } = this.props;
    const { error, retryCount, subtreeKey } = this.state;

    if (error) {
      if (!isKnownMoonPayCrash(error)) {
        throw error;
      }

      if (retryCount > 0) {
        return <MoonPayCrashFallback />;
      }

      return null;
    }

    return <React.Fragment key={subtreeKey}>{children}</React.Fragment>;
  }
}

function MoonPayCrashFallback() {
  return (
    <div className="min-h-svh bg-zinc-950 text-zinc-50 flex items-center justify-center px-6">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-black/40 p-6 text-center shadow-2xl">
        <p className="text-lg font-semibold">Payment flow closed unexpectedly</p>
        <p className="mt-2 text-sm text-zinc-300">
          The app recovered once, but the wallet flow failed again. Reload to
          continue.
        </p>
        <DefaultButton
          className="mt-5 w-full h-11 text-sm"
          onClick={() => window.location.reload()}
        >
          Reload App
        </DefaultButton>
      </div>
    </div>
  );
}

export function MoonPayRecoveryToastNotifier() {
  React.useEffect(() => {
    const recoveryMarker = window.sessionStorage.getItem(
      RECOVERY_NOTICE_STORAGE_KEY,
    );

    if (!recoveryMarker) {
      return;
    }

    window.sessionStorage.removeItem(RECOVERY_NOTICE_STORAGE_KEY);
    toast("Payment flow closed. The app recovered.");
  }, []);

  return null;
}
